/*
  adg-action-bar — behaviour for the shared bottom bar.

  Handles every instance on the page, including the BYO submit bar, which
  carries the same [data-adg-actionbar] hook rather than being a lookalike.

  Three jobs:
    1. Top      — scroll to the top of the page.
    2. Back     — navigate if given a href, otherwise fire
                  `adg-back-to-collections` and let the host swap in place.
                  That is the difference that makes it useful inside BYO: you
                  drop back to the collection grid WITHOUT leaving your build,
                  so the picks tray and everything in it survive.
    3. Reveal   — a utility-only bar stays off screen until there is somewhere
                  to scroll back to. A bar with a primary action in it (BYO's
                  Add to Cart) is marked `always` and never hides.

  It also publishes the bar's height as --adg-actionbar-h and pads the page by
  it, so a fixed bar never sits on top of the last row of content or the
  footer. Justin asked for space below the footer; this is that.
*/
(function () {
  'use strict';

  var REVEAL_AT = 1.5;   // screens scrolled before a utility-only bar appears

  /* Is there genuinely a step UP the shopper's path from here? THE single
     rule every back control on the site answers to — the global bar, the
     section bars, and the builders' cart bar all ask this one function. */
  function canStepUp() {
    var prevUp = (window.ADGScrollMemory && window.ADGScrollMemory.previous)
      ? window.ADGScrollMemory.previous() : '';
    var offer;
    if (prevUp) {
      offer = prevUp !== window.location.pathname;
    } else {
      var ref = document.referrer;
      offer = !!(ref && ref.indexOf(window.location.origin) === 0 &&
                 ref.split('#')[0] !== window.location.href.split('#')[0]);
    }
    return offer && window.history.length > 1;
  }
  window.ADGActionBar = { canStepUp: canStepUp };

  function scrollTop() {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }

  function wire(bar) {
    if (bar.dataset.adgActionbarWired) return;
    bar.dataset.adgActionbarWired = '1';

    var top = bar.querySelector('[data-adg-actionbar-top]');
    if (top) top.addEventListener('click', scrollTop);

    var back = bar.querySelector('[data-adg-actionbar-back]');

    // WHO OWNS THIS BUTTON. Inside a builder, "back" is a STEP CHANGE — the
    // in-place swap to the collection tiles that keeps the shopper's picks
    // alive. It is never a navigation, so the journey-memory fill below must
    // never touch it. It did, once: a tile clicked anywhere on the site
    // within ten minutes left an entry, the fill wrote that page onto the
    // builder's hidden back button, the builder relabelled it "Collections",
    // and tapping it walked the shopper off the builder and threw away their
    // picks (Joel's "won't switch collection to collection", reproduced
    // 08-07). Host-owned buttons carry data-byo-back; the bar keeps its
    // hands off them, filling AND navigating.
    var hostOwned = back && back.hasAttribute('data-byo-back');

    // THE WAY BACK FILLS ITSELF, HERE, IN THE SHARED BAR — not in any one
    // page's script. The design grids fill it from ?from=; every other
    // surface gets it from the shared journey memory below. This is the fix
    // for the twenty product-format pages (metal emblem signs, puzzles,
    // books, apparel…) that rendered the bar but had nothing to fill the
    // back control, because the filler lived inside the design grid's JS —
    // found by Justin, 08-06, after two days of this class of miss.
    // From now on: render the bar, and the way back works. No opt-in.
    if (back && !hostOwned && back.hidden && !back.dataset.href && window.ADGScrollMemory) {
      var o = window.ADGScrollMemory.origin();
      if (o && o.path) {
        back.dataset.href = o.path;
        var backLbl = back.querySelector('.adg-actionbar__label');
        // Never leave the Liquid default standing on a filled control — a
        // button that says "All Collections" while pointing at Canvas
        // Prints is worse than one that just says "Back".
        if (backLbl) backLbl.textContent = o.label || 'Back';
        back.hidden = false;
      }
    }

    // THE WAY BACK IS ALWAYS THERE (Justin, 08-07): when nothing names the
    // origin — no ?from=, no journey entry that survived the guards — the
    // control falls back to the browser's own history, exactly like the
    // browser Back button, labelled plainly "Back". Sometimes-a-button,
    // sometimes-not is what made the system feel broken. The ONE case it
    // stays hidden: there is genuinely nowhere to go (direct entry, new
    // tab) — a button that does nothing is worse than no button.
    // THE ONE VISIBILITY RULE, host-owned buttons included: is there
    // genuinely a step UP the shopper's path? The trail decides; the
    // referrer is only consulted where the trail is empty (first page of
    // an old session). Filling a host-owned button is safe — its CLICK
    // always goes to the host, which reinterprets back only inside its own
    // sub-step (BYO's designs panel) and defers to history everywhere else.
    if (back && back.hidden && !back.dataset.href && !back.dataset.history) {
      if (canStepUp()) {
        back.dataset.history = '1';
        var gl = back.querySelector('.adg-actionbar__label');
        if (gl) gl.textContent = 'Back';
        back.hidden = false;
      }
    }

    if (back) {
      back.addEventListener('click', function () {
        // Host-owned: the event is the ONLY correct behaviour, whatever any
        // filler may have written onto the dataset in the meantime.
        var href = hostOwned ? null : back.dataset.href;
        if (href) {
          // PREFER THE BROWSER'S OWN BACK. If the page we came from IS the
          // one this button points at, history.back() is a real history
          // navigation — no new entry, so Back can never start ping-ponging
          // between two pages (the loop Justin hit: a named back that
          // PUSHED, then plain history-back bounced between the pair).
          // The journey trail is the truth about where we came from;
          // document.referrer is only the fallback, since it lies after
          // bfcache restores and redirects.
          var target = href.split('?')[0];
          var prevPath = (window.ADGScrollMemory && window.ADGScrollMemory.previous)
            ? window.ADGScrollMemory.previous() : '';
          var cameFromThere;
          if (prevPath) {
            cameFromThere = prevPath === target;
          } else {
            var sameOrigin = document.referrer.indexOf(window.location.origin) === 0;
            cameFromThere = sameOrigin &&
              document.referrer.slice(window.location.origin.length).split('?')[0] === target;
          }
          if (cameFromThere && window.history.length > 1) {
            window.history.back();
            return;
          }
          if (window.ADGScrollMemory) window.ADGScrollMemory.expectReturn();
          window.location.href = href;
          return;
        }
        // The shopper arrived from somewhere on this site but nothing named
        // where, so "back" is exactly the browser's own Back — which also
        // restores their scroll position for free.
        if (!hostOwned && back.dataset.history === '1' && window.history.length > 1) {
          window.history.back();
          return;
        }
        // No href at all: this page swaps in place. The host listens for this.
        back.dispatchEvent(new CustomEvent('adg-back-to-collections', {
          bubbles: true, cancelable: true
        }));
      });
    }

    // THE BAR IS ON WHENEVER IT HAS SOMETHING TO SAY.
    // "Back to Canvas Prints" is worth saying from the first paint — making
    // the shopper scroll to discover the way back is the opposite of the
    // point. Top is different: there is nothing above you yet, so it slides
    // in the moment there is, which lands the movement exactly when it has
    // become the thing you want.
    var topWrap = bar.querySelector('[data-adg-actionbar-topwrap]');
    var hardAlways = bar.hasAttribute('data-adg-actionbar-always') ||
                     !bar.classList.contains('adg-actionbar--utility-only');

    function barHasContent() {
      return hardAlways || (back && !back.hidden);
    }

    // Re-pad the moment the bar's visibility flips. The padding used to be
    // computed only at load — right for bars that reveal at boot (a filled
    // back control), but a bar that slides in MID-SCROLL on a plain page
    // would sit over the footer's last row (Justin, 08-08: never cover
    // anything). Tracking the flip keeps the page clear in both directions.
    var wasVisible = null;
    var onScroll = function () {
      var past = window.scrollY > window.innerHeight * REVEAL_AT;
      if (topWrap) topWrap.classList.toggle('is-visible', past);
      if (!hardAlways) bar.classList.toggle('is-visible', past || barHasContent());
      var visibleNow = hardAlways || bar.classList.contains('is-visible');
      if (visibleNow !== wasVisible) { wasVisible = visibleNow; padPage(); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // The back control is revealed later by the host, once it knows where
    // "back" goes (adg-artgrid reads that from ?from= after its fetch). Watch
    // for it rather than guessing at a delay.
    if (back && 'MutationObserver' in window) {
      new MutationObserver(onScroll).observe(back, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  /* Keep the page clear of the bar. Measured rather than assumed, because the
     bar is a different height on a phone and different again with a cart form
     in it. Only the tallest bar counts if a page somehow has two. */
  function padPage() {
    var tallest = 0;
    document.querySelectorAll('[data-adg-actionbar]').forEach(function (b) {
      // For the reveal-on-scroll bars the CLASS is the source of truth —
      // in the exact frame the reveal starts, computed visibility can still
      // read 'hidden' (the transition hasn't ticked), and trusting it left
      // the page unpadded under a visible bar. Computed style still decides
      // for always-on bars (a display:none template branch etc.).
      if (b.classList.contains('adg-actionbar--utility-only')) {
        if (!b.classList.contains('is-visible')) return;
      } else {
        var cs = getComputedStyle(b);
        if (cs.display === 'none' || cs.visibility === 'hidden') return;
      }
      var h = b.getBoundingClientRect().height;
      if (h > tallest) tallest = h;
    });
    var px = Math.round(tallest);
    document.documentElement.style.setProperty('--adg-actionbar-h', px + 'px');
    // Pad by the WHOLE covered band, not just the bar (WCAG 2.4.11,
    // 2026-10-07). The bar sits on --adg-inset-0, above whatever foreign
    // thing is docked under it (Shopify's cookie banner, the preview bar),
    // so what hides the end of the page is that band plus the bar. Padding
    // by the bar alone left the last footer links under it, unreachable
    // by focus, while the cookie banner was unanswered. The variable is
    // live (adg-bottom-inset.js re-measures on every mutation), so the
    // padding follows the banner in and out without this re-running.
    if (document.body) document.body.style.paddingBottom = 'calc(' + px + 'px + var(--adg-inset-0, 0px))';
  }

  function boot() {
    // ONE bar per page. theme.liquid renders a global utility bar on every
    // page (Justin, 08-08: back + top everywhere, history-back on plain
    // pages); a page that renders its OWN bar — the custom grids, the
    // builders' cart bar — wins, and the global one removes itself.
    var bars = document.querySelectorAll('[data-adg-actionbar]');
    if (bars.length > 1) {
      var hasOwn = false;
      bars.forEach(function (b) {
        if (!b.hasAttribute('data-adg-actionbar-global')) hasOwn = true;
      });
      if (hasOwn) {
        bars.forEach(function (b) {
          if (b.hasAttribute('data-adg-actionbar-global')) b.remove();
        });
      }
    }
    document.querySelectorAll('[data-adg-actionbar]').forEach(wire);
    padPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  window.addEventListener('resize', padPage, { passive: true });

  // The BYO bar changes height when its label wraps, and a page can grow a bar
  // after load (the builder swapping panels).
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(padPage);
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-adg-actionbar]').forEach(function (b) { ro.observe(b); });
    });
  }
})();
